import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, ArrowRight, Building2, Factory, Stethoscope, GraduationCap } from 'lucide-react';
const regions = [{
  title: 'Birmingham & Black Country',
  description: 'Supporting major office developments, retail parks, and the extensive industrial estates across the Black Country.',
  towns: ['Birmingham', 'Blackheath', 'Bloxwich', 'Bridgtown', 'Brierley Hill', 'Brownhills', 'Cradley Heath', 'Dudley', 'Halesowen', 'Kingswinford', 'Oldbury', 'Rowley Regis', 'Sedgley', 'Smethwick', 'Stourbridge', 'Tipton', 'Walsall', 'Wednesbury', 'Wednesfield', 'West Bromwich', 'Willenhall', 'Wolverhampton', 'Bilston']
}, {
  title: 'Warwickshire & surrounding',
  description: 'Serving corporate headquarters, logistics hubs, and commercial properties along the M40 corridor and beyond.',
  towns: ['Coventry', 'Nuneaton', 'Rugby', 'Royal Leamington Spa', 'Warwick', 'Kenilworth', 'Alcester', 'Henley-in-Arden', 'Shipston-on-Stour', 'Southam', 'Studley', 'Wellesbourne', 'Stratford-upon-Avon', 'Bedworth', 'Atherstone', 'Coleshill', 'Solihull']
}, {
  title: 'Staffordshire & Stoke-on-Trent',
  description: 'Providing robust HVAC solutions for manufacturing facilities, distribution centres, and business parks.',
  towns: ['Stafford', 'Cannock', 'Rugeley', 'Penkridge', 'Stone', 'Uttoxeter', 'Burton upon Trent', 'Stoke-on-Trent', 'Newcastle-under-Lyme', 'Kidsgrove', 'Biddulph', 'Leek', 'Cheadle', 'Tamworth', 'Lichfield', 'Burntwood', 'Aldridge', 'Sutton Coldfield']
}];
const faqs = [{
  q: 'Do you cover smaller towns like Atherstone, Bedworth, Shipston-on-Stour and Southam?',
  a: 'Yes, we cover all these towns and the surrounding areas. Our Coventry headquarters gives us rapid access across the entire Midlands region for commercial AC installation and maintenance.'
}, {
  q: 'Do you undertake residential air conditioning work?',
  a: 'No. We work exclusively with commercial B2B clients, including FM companies, M&E contractors, landlords, and direct business owners. This allows us to maintain the highest standards of commercial compliance and support.'
}, {
  q: 'Do you provide TM44 AC inspections across the Midlands?',
  a: 'Yes. We arrange comprehensive TM44 inspections for any commercial building with a combined cooling capacity over 12kW, ensuring you remain fully compliant with current regulations.'
}, {
  q: 'How quickly can you attend a commercial site survey?',
  a: 'We typically arrange site surveys within 48 hours of your enquiry. For urgent reactive support for contracted clients, we offer a 4-hour response time.'
}];
export function ServiceAreasHub() {
  return <main className="min-h-screen font-sans bg-white pt-28" data-id="element-1961">
      {/* Hero Section */}
      <section className="bg-primary-dark text-white py-20 relative overflow-hidden" data-id="element-1962">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-accent/5 rounded-bl-full blur-3xl transform translate-x-1/3 -translate-y-1/4 pointer-events-none" data-id="element-1963"></div>
        <div className="container mx-auto px-6 relative z-10" data-id="element-1964">
          <div className="max-w-3xl" data-id="element-1965">
            <span className="text-accent font-semibold tracking-wider uppercase text-sm mb-4 block" data-id="element-1966">
              Areas We Cover
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight" data-id="element-1967">
              Commercial HVAC across the Midlands & Nationwide
            </h1>
            <p className="text-lg text-white/70 leading-relaxed mb-8" data-id="element-1968">
              Based in Coventry, AccuTemp provides commercial air conditioning
              installation, maintenance, TM44 inspections, and ventilation
              services across the Midlands and nationwide where commercially
              viable. We partner exclusively with FM companies, M&E contractors,
              landlords, and commercial property owners.
            </p>
            <div className="flex flex-wrap gap-4" data-id="element-1969">
              <Link to="/contact" className="bg-accent hover:bg-accent/90 text-white px-8 py-4 rounded-full font-bold transition-colors inline-flex items-center gap-2" data-id="element-1970">
                Request a commercial site survey{' '}
                <ArrowRight className="w-5 h-5" data-id="element-1971" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="py-12 border-b border-gray-100 bg-surface/30" data-id="element-1972">
        <div className="container mx-auto px-6" data-id="element-1973">
          <p className="text-sm font-bold uppercase tracking-widest text-primary-dark/40 mb-6 text-center" data-id="element-1974">
            We support commercial sites including
          </p>
          <div className="flex flex-wrap justify-center gap-8 md:gap-16 opacity-70" data-id="element-1975">
            <div className="flex items-center gap-2 text-primary-dark font-medium" data-id="element-1976">
              <Building2 className="w-5 h-5 text-accent" data-id="element-1977" /> Offices & Retail
            </div>
            <div className="flex items-center gap-2 text-primary-dark font-medium" data-id="element-1978">
              <Factory className="w-5 h-5 text-accent" data-id="element-1979" /> Industrial &
              Warehouses
            </div>
            <div className="flex items-center gap-2 text-primary-dark font-medium" data-id="element-1980">
              <Stethoscope className="w-5 h-5 text-accent" data-id="element-1981" /> Healthcare
            </div>
            <div className="flex items-center gap-2 text-primary-dark font-medium" data-id="element-1982">
              <GraduationCap className="w-5 h-5 text-accent" data-id="element-1983" /> Education
            </div>
          </div>
        </div>
      </section>

      {/* Regions Grid */}
      <section className="py-24" data-id="element-1984">
        <div className="container mx-auto px-6" data-id="element-1985">
          <div className="grid lg:grid-cols-3 gap-12" data-id="element-1986">
            {regions.map((region, idx) => <motion.div key={region.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="bg-white rounded-3xl p-8 shadow-lg border border-primary-dark/5" data-id="element-1987">
                <div className="w-12 h-12 bg-primary-light rounded-xl flex items-center justify-center mb-6" data-id="element-1988">
                  <MapPin className="w-6 h-6 text-accent" data-id="element-1989" />
                </div>
                <h2 className="text-2xl font-bold text-primary-dark mb-4" data-id="element-1990">
                  {region.title}
                </h2>
                <p className="text-gray-600 mb-8 leading-relaxed" data-id="element-1991">
                  {region.description}
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-1992">
                  {region.towns.map(town => {
                // Make flagship cities clickable links
                const flagshipCities = ['Birmingham', 'Coventry', 'Solihull', 'Wolverhampton', 'Stoke-on-Trent', 'Lichfield', 'Royal Leamington Spa', 'Stratford-upon-Avon', 'Stafford', 'Burton upon Trent'];
                const isFlagship = flagshipCities.includes(town);
                if (isFlagship) {
                  return <Link key={town} to={`/service-areas/${town.toLowerCase().replace(/\s+/g, '-')}`} className="px-3 py-1.5 bg-primary-dark text-white rounded-md text-sm font-medium hover:bg-accent transition-colors" data-id="element-1993">
                          {town}
                        </Link>;
                }
                return <span key={town} className="px-3 py-1.5 bg-surface text-primary-dark/70 rounded-md text-sm" data-id="element-1994">
                        {town}
                      </span>;
              })}
                </div>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mt-16 bg-primary-dark text-white rounded-3xl p-10 text-center max-w-4xl mx-auto" data-id="element-1995">
            <h3 className="text-2xl font-bold mb-4" data-id="element-1996">
              Other areas & national projects
            </h3>
            <p className="text-white/70 text-lg mb-8" data-id="element-1997">
              We regularly handle multi-site and national rollouts for our
              commercial partners. If your town is not listed above, we probably
              still cover it. Tell us about your building and we'll confirm.
            </p>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-white px-8 py-4 rounded-full font-bold transition-colors" data-id="element-1998">
              Discuss your project <ArrowRight className="w-5 h-5" data-id="element-1999" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-24 bg-surface/50" data-id="element-2000">
        <div className="container mx-auto px-6 max-w-4xl" data-id="element-2001">
          <h2 className="text-3xl md:text-4xl font-bold text-primary-dark mb-12 text-center" data-id="element-2002">
            Frequently asked questions
          </h2>
          <div className="space-y-6" data-id="element-2003">
            {faqs.map((faq, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-white rounded-2xl p-8 shadow-sm border border-primary-dark/5" data-id="element-2004">
                <h3 className="text-xl font-bold text-primary-dark mb-3" data-id="element-2005">
                  {faq.q}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2006">{faq.a}</p>
              </motion.div>)}
          </div>
        </div>
      </section>
    </main>;
}